<script lang="ts" setup>
import SaleCustomers from './SaleCustomers.vue'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { CustomerInterface } from '../../models/CustomerModel'
import SaleProductSelection from './SaleProductSelection.vue'
import { ProductInterface } from '../../models/ProductModel'
import SaleProductEdit from './SaleProductEdit.vue'
import { Modal } from 'bootstrap'
import AButton from '../common/AButton.vue'
import { completeSale } from '../../services/saleApi'
import { SaleInterface } from '../../models/SaleModel'
import { showToast } from '../../services/toast'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

type BsModal = InstanceType<typeof Modal>

const props = withDefaults(defineProps<Props>(), {
  saleData: null,
})

interface Props {
  saleData?: SaleInterface | null
}

const { t } = useI18n()
const selectedCustomer = ref<CustomerInterface | null>(null)
const activeProduct = ref<ProductInterface | null>(null)
const selectedProducts = ref<ProductInterface[]>([])
const clientDeleteModalRef = ref<HTMLElement | null>(null)
const clientDeleteModal = ref<BsModal | null>(null)
const finishSaleModalRef = ref<HTMLElement | null>(null)
const finishSaleModal = ref<BsModal | null>(null)
const isSubmitting = ref<boolean>(false)
const initialCustomerLoad = ref<boolean>(true)
const router = useRouter()

function setInitialData() {
  if (!props.saleData) return

  if (props.saleData.customer_id) {
    selectedCustomer.value = {
      id: props.saleData.customer_id,
      name: props.saleData.customer_name || '',
      code: props.saleData.customer_code || '',
      address: '',
    }
  }

  if (props.saleData.items && props.saleData.items.length) {
    props.saleData.items.forEach((preloadedProduct) =>
      selectedProducts.value.push({
        id: preloadedProduct.product_id,
        name: preloadedProduct.product_name,
        code: preloadedProduct.product_code,
        barcode: '',
        quantity: preloadedProduct.quantity,
      })
    )
  }
}

function setSelectedCustomer(customer: CustomerInterface) {
  initialCustomerLoad.value = false
  selectedCustomer.value = customer

  if (props.saleData) {
    completeCurrentSale(true)
  }
}

function removeSelectedCustomer() {
  selectedCustomer.value = null
  closeClientDeleteModal()
}

function editProduct(product: ProductInterface) {
  activeProduct.value = product
}

function updateProductList(product: ProductInterface) {
  const index = selectedProducts.value.findIndex((p) => p.id === product.id)

  if (product.quantity === 0) {
    if (index !== -1) {
      selectedProducts.value.splice(index, 1)
    }
    activeProduct.value = null
    return
  }

  if (index !== -1) {
    selectedProducts.value[index] = product
  } else {
    selectedProducts.value.push(product)
  }

  activeProduct.value = null
}

function completeCurrentSale(noRedirect = false) {
  const saleData: SaleInterface = {
    customer_id: selectedCustomer.value?.id,
    items: [],
  }
  selectedProducts.value.forEach((product) => {
    saleData.items.push({
      product_id: String(product.id),
      quantity: Number(product.quantity),
    })
  })
  isSubmitting.value = true
  completeSale(saleData, props.saleData?.sale_id)
    .then(() => {
      if (!noRedirect) {
        selectedCustomer.value = null
        selectedProducts.value = []
      }
      closeFinishSaleModal()
      showToast({
        message: t(props.saleData ? 'sale-update-success' : 'sale-finish-success'),
        variant: 'success',
        noTitle: true,
      })
    })
    .finally(() => {
      if (!props.saleData || !noRedirect) {
        router.push({ name: 'dashboard' })
      }
      isSubmitting.value = false
    })
}

onMounted(() => {
  setInitialData()

  if (clientDeleteModalRef.value) {
    clientDeleteModal.value = new Modal(clientDeleteModalRef.value, {
      backdrop: true,
      keyboard: true,
    })
  }

  if (finishSaleModalRef.value) {
    finishSaleModal.value = new Modal(finishSaleModalRef.value, {
      backdrop: true,
      keyboard: true,
    })
  }
})

onBeforeUnmount(() => {
  clientDeleteModal.value?.dispose()
  finishSaleModal.value?.dispose()
})

function openClientDeleteModal() {
  clientDeleteModal.value?.show()
}

function closeClientDeleteModal() {
  clientDeleteModal.value?.hide()
}

function openFinishSaleModal() {
  finishSaleModal.value?.show()
}

function closeFinishSaleModal() {
  finishSaleModal.value?.hide()
}
</script>

<template>
  <div v-if="selectedCustomer">
    <template v-if="!activeProduct">
      <div class="d-flex justify-content-between align-items-center gap-12 mb-12">
        <div>
          <div>{{ $t('customer') }}:</div>
          <div>
            <b>{{ selectedCustomer.name }}</b>
          </div>
        </div>
        <button class="button-icon button-icon--clear" @click="openClientDeleteModal" />
      </div>
      <hr />
    </template>
    <SaleProductSelection
      v-if="!activeProduct"
      :selected-products="selectedProducts"
      @product-selected="editProduct"
    />
    <div v-if="selectedProducts && selectedProducts.length && !activeProduct">
      <hr />
      <div class="mb-12">{{ $t('selected-products') }}:</div>
      <div>
        <div
          v-for="(product, productIndex) in selectedProducts"
          :key="productIndex"
          class="product-item"
        >
          <div>
            <b>{{ product.name }}</b>
            <div>
              <small
                >{{ $t('code') }}:&nbsp;{{ product.code }} | {{ $t('barcode') }}:&nbsp;{{
                  product.barcode
                }}</small
              >
            </div>
            <div>
              {{ $t('quantity') }}: <b>{{ product.quantity }}</b>
            </div>
          </div>
          <button
            class="button-icon button-icon--edit"
            @click="
              () => {
                editProduct(product)
              }
            "
          />
        </div>
      </div>
    </div>
    <div v-if="selectedProducts.length && !activeProduct" class="mb-12">
      <AButton
        class="btn-warning w-100 mt-12"
        :disabled="!selectedProducts.length"
        @click="openFinishSaleModal"
      >
        {{ $t(props.saleData ? 'update-sale' : 'finish-sale') }}
      </AButton>
    </div>
  </div>
  <SaleCustomers v-if="!selectedCustomer" @customer-selected="setSelectedCustomer" />
  <SaleProductEdit
    v-if="activeProduct"
    :product="activeProduct"
    @product-updated="updateProductList"
  />

  <div ref="clientDeleteModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body">
          <h4 class="mb-16">{{ $t('confirm-customer-delete-header') }}</h4>
          <p class="mb-32">{{ $t('confirm-customer-delete-text') }}</p>
          <div class="d-flex flex-wrap gap-12">
            <button type="button" class="btn btn-outline-secondary flex-1" data-bs-dismiss="modal">
              {{ $t('cancel') }}
            </button>
            <button type="button" class="btn btn-danger flex-1" @click="removeSelectedCustomer">
              {{ $t('finish-order-confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div ref="finishSaleModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body">
          <h4 class="mb-16">
            {{ $t(props.saleData ? 'update-sale-header' : 'finish-sale-header') }}
          </h4>
          <p class="mb-32">{{ $t(props.saleData ? 'update-sale-text' : 'finish-sale-text') }}</p>
          <div class="d-flex flex-wrap gap-12">
            <button type="button" class="btn btn-outline-secondary flex-1" data-bs-dismiss="modal">
              {{ $t('cancel') }}
            </button>
            <AButton
              type="button"
              class="btn btn-danger flex-1"
              @click="
                () => {
                  completeCurrentSale()
                }
              "
            >
              {{ $t('finish-order-confirm') }}
            </AButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
